<script>
/**
 * @component ConversacionesIaPanel
 * @description Panel lateral del SeguimientoTab rediseñado (UC-REG-065) que
 *              lista las últimas N healthConversations del paciente con
 *              detalle inline expandible.
 *
 *              La primera está expandida por default; el resto colapsado.
 *              Click en header de cualquier conversation alterna expandido.
 *
 *              Reusa `HealthConversationResultDetail.vue` para el detalle
 *              expandido (transcript + signals + justificación). Si el
 *              paciente nunca tuvo healthConversation, muestra empty state.
 *
 * @example <ConversacionesIaPanel :conversations="conversations" :loading="false" />
 */
import { formatAR } from "@/services/datetime";
import HealthConversationResultDetail from "@/components/patient/dashboard/HealthConversationResultDetail.vue";
import HealthScoreBadge from "@/components/ui/HealthScoreBadge.vue";

export default {
  name: "ConversacionesIaPanel",

  components: { HealthConversationResultDetail, HealthScoreBadge },

  props: {
    /**
     * @description Array de HealthConversationTask docs con .result populado.
     *              Si está vacío, panel muestra empty state.
     */
    conversations: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      expandedIds: new Set(),
    };
  },

  watch: {
    /**
     * @description Al primer load, expandir la primera conversation.
     */
    conversations: {
      handler(newVal) {
        if (newVal.length > 0 && this.expandedIds.size === 0) {
          this.expandedIds = new Set([newVal[0]._id]);
        }
      },
      immediate: true,
    },
  },

  methods: {
    /**
     * @description Toggle expand/collapse de una conversation.
     */
    toggle(id) {
      const next = new Set(this.expandedIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      this.expandedIds = next;
    },
    isExpanded(id) {
      return this.expandedIds.has(id);
    },
    /**
     * @description "30/5 13:38" — fecha corta + hora.
     */
    /**
     * @description UC-REG-079: TZ del tenant.
     */
    formatDate(date) {
      if (!date) return "—";
      return formatAR(date, "D/M HH:mm");
    },
    /**
     * @description Labels legibles de closedReason.
     */
    closedReasonLabel(reason) {
      const map = {
        ai_sufficient: "info suficiente",
        max_turns: "tope de turnos",
        patient_silent: "sin respuesta",
        urgent: "urgencia",
        error: "error",
      };
      return map[reason] || reason || "—";
    },
  },
};
</script>

<template lang="pug">
  .conversaciones-panel
    .conversaciones-panel__header
      span.conversaciones-panel__title Conversaciones de salud
      span.conversaciones-panel__count(v-if="conversations.length > 0") {{ conversations.length }}

    .conversaciones-panel__loading(v-if="loading")
      i.el-icon-loading
      span Cargando…

    .conversaciones-panel__empty(v-else-if="!conversations.length")
      i.el-icon-chat-line-square
      span Aún no hay conversaciones de salud para este paciente.

    .conversaciones-panel__list(v-else)
      .conversation-item(
        v-for="conv in conversations"
        :key="conv._id"
        :class="{ 'conversation-item--expanded': isExpanded(conv._id) }"
      )
        .conversation-item__head(@click="toggle(conv._id)" role="button")
          i.conversation-item__chevron(:class="isExpanded(conv._id) ? 'el-icon-arrow-down' : 'el-icon-arrow-right'")
          span.conversation-item__date {{ formatDate(conv.completedAt) }}
          HealthScoreBadge(
            :score="conv.result && conv.result.score"
            :semaphore="conv.result && conv.result.semaphore"
            size="sm"
          )
          span.conversation-item__name {{ conv.name }}
          span.conversation-item__meta {{ (conv.result && conv.result.turnsUsed) || 0 }}t · {{ closedReasonLabel(conv.result && conv.result.closedReason) }}

        .conversation-item__body(v-if="isExpanded(conv._id)")
          HealthConversationResultDetail(:task="conv")
</template>

<style lang="scss" scoped>
.conversaciones-panel {
  font-family: "Plus Jakarta Sans", sans-serif;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 800px;

  &__header {
    padding: 14px 16px;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    gap: 8px;
    background: #fbfcfe;
  }
  &__title {
    font-size: 14px;
    font-weight: 700;
    color: #0f1e36;
  }
  &__count {
    font-size: 11px;
    color: #6b7280;
    background: #f3f4f6;
    padding: 1px 8px;
    border-radius: 10px;
    font-weight: 600;
  }
  &__loading,
  &__empty {
    padding: 32px;
    text-align: center;
    color: #9ca3af;
    font-size: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    i {
      font-size: 18px;
    }
  }
  &__list {
    overflow-y: auto;
    flex: 1;
  }
}

.conversation-item {
  border-bottom: 1px solid #f0f2f7;

  &:last-child {
    border-bottom: none;
  }

  &__head {
    padding: 10px 16px;
    display: grid;
    grid-template-columns: 16px 60px auto 1fr auto;
    gap: 8px;
    align-items: center;
    cursor: pointer;
    transition: background 0.1s ease;
    &:hover {
      background: #fafbfd;
    }
  }
  &__chevron {
    color: #9ca3af;
    font-size: 12px;
  }
  &__date {
    font-size: 11px;
    color: #6b7280;
    font-variant-numeric: tabular-nums;
    font-weight: 600;
  }
  &__name {
    font-size: 12px;
    color: #111827;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  &__meta {
    font-size: 10px;
    color: #9ca3af;
    text-align: right;
  }
  &__body {
    padding: 8px 16px 16px;
    background: #fbfcfe;
    border-top: 1px solid #f0f2f7;
  }

  &--expanded {
    background: #fbfcfe;
  }
}
</style>
